/**
 * Creates an activity-based timer system that exposes an interface similar to setInterval, 
 * but with no guarantee that code will actually execute at the specified interval.
 * 
 * Instead of relying on native timers for execution, this system requires manual checking
 * via the `runTimersCheck()` function. When called, it loops through all registered timers,
 * checks when they were last executed, and if more time than the defined interval has passed,
 * it executes the callback.
 * 
 * Each timer can optionally use a "forced" interval parameter that creates
 * a native setInterval underneath, providing a more reliable guarantee of running every
 * x milliseconds regardless of manual checks.
 * 
 * Useful for scenarios where you want to check for updates periodically
 * but need fine-grained control over when those checks actually occur.
 * 
 * @returns {Object} Timer management object with the following methods:
 * @returns {Function} setInterval - Creates a new timer with callback, interval, and optional forcedInterval
 * @returns {Function} clearInterval - Removes a timer by its ID and cleans up any associated intervals  
 * @returns {Function} runTimersCheck - Manually triggers a check for all timers to see if they should execute
 * 
 * @example
 * const timer = ActivityBasedTimer();
 * 
 * // Create a timer that only runs when manually checked
 * const timerId = timer.setInterval({
 *   callback: ({ forced, timerId }) => console.log('Manual check executed'),
 *   interval: 5000 // Will only run when runTimersCheck() is called and 5+ seconds have passed
 * });
 * 
 * // Create a timer with both manual and forced intervals
 * const forcedTimerId = timer.setInterval({
 *   callback: ({ forced, timerId }) => console.log('Timer executed, forced:', forced),
 *   interval: 10000,     // Manual check interval: 10 seconds
 *   forcedInterval: 2000 // Native setInterval: runs every 2 seconds regardless
 * });
 * 
 * // Manually check all timers (call this in your app's update loop)
 * timer.runTimersCheck();
 * 
 * // Cleanup
 * timer.clearInterval(timerId);
 * timer.clearInterval(forcedTimerId);
 */
const ActivityBasedTimer = () => {
  let globalTimerId = 0;
  const timers = new Map();

  const _maybeExecuteTimerCallback = ({ timerId, forced = false }) => {
    const timer = timers.get(timerId);

    if (timer === undefined) {
      return;
    }

    const {
      callback,
      interval,
      forcedInterval,
      forcedIntervalId,
      lastExecution,
    } = timer;
    const intervalToCheckFor = forced === true
      ? forcedInterval
      : interval;
    const now = Date.now();

    if (now - lastExecution < intervalToCheckFor) {
      return;
    }

    const newTimer = {
      ...timer,
      lastExecution: now,
    };

    if (forcedIntervalId !== undefined) {
      window.clearInterval(forcedIntervalId);
      newTimer.forcedIntervalId = window.setInterval(() => {
        _maybeExecuteTimerCallback({ timerId, forced: true });
      }, forcedInterval);
    }

    timers.set(timerId, newTimer);
    callback({ forced, timerId });
  };

  const setInterval = ({ callback, interval, forcedInterval } = {}) => {
    const timerId = globalTimerId;

    if (typeof callback !== 'function' || typeof interval !== 'number') {
      return undefined;
    }

    const timer = {
      callback,
      interval,
      lastExecution: Date.now(),
    };

    if (forcedInterval !== undefined) {
      timer.forcedInterval = forcedInterval;
      timer.forcedIntervalId = window.setInterval(() => {
        _maybeExecuteTimerCallback({ timerId, forced: true });
      }, forcedInterval);
    }

    timers.set(timerId, timer);
    globalTimerId += 1;
    return timerId;
  };

  const clearInterval = (timerId) => {
    const timer = timers.get(timerId);

    if (timer === undefined) {
      return;
    }

    const { forcedIntervalId } = timer;

    if (forcedIntervalId !== undefined) {
      window.clearInterval(forcedIntervalId);
    }

    timers.delete(timerId);
  };

  const runTimersCheck = () => {
    timers.forEach((_timer, timerId) => {
      _maybeExecuteTimerCallback({ timerId });
    });
  };

  return {
    setInterval,
    clearInterval,
    runTimersCheck,
  };
};

export default ActivityBasedTimer;
